import { PromptResultCreate } from '@common/types';
import { BoxItem } from 'box-ui-elements/es';
import { useCallback } from 'react';
import { BoxTemplateLabels, ExtractionType, FileType, PromptResultFeature, UserRole } from 'enums';
import { useHotjar, useMutatePromptResult, useMutateQuote, useToast } from 'hooks';
import { extractErrorMessage, getBoxClassifiedByRoleMetadata, getFileMetadataField, logger } from 'utils';
import { HotjarEvents } from 'utils/hotjar-events';
import { BrokerUrlParams } from 'broker/broker-routes';
import { ExtendedBoxItem } from 'broker/components/FilesExplorer/types';
import { useFileActions, useUpdateQueryParam } from 'broker/hooks';
import { useFileOperationOverlay } from './FileOperationOverlayProvider';
import { useCreateTruncatedPdf } from './useCreateTruncatedPdf';

interface FileActionProps {
  afterFileOperation: () => Promise<void>;
  selectedFiles?: ExtendedBoxItem[];
  setSelectedFiles?: (boxItems: ExtendedBoxItem[]) => void;
  submissionId?: string;
}

export function useFileActionsCallbacks({
  afterFileOperation,
  selectedFiles,
  setSelectedFiles,
  submissionId,
}: FileActionProps) {
  const hotjar = useHotjar();
  const updateQueryParam = useUpdateQueryParam();
  const { createPromptResult } = useMutatePromptResult();
  const { extractQuoteData } = useMutateQuote();
  const { showToast } = useToast();
  const truncatePdf = useCreateTruncatedPdf();
  const { showProgressOverlay, updateProgress, hideOverlay } = useFileOperationOverlay();

  const { deleteFile, downloadFile, renameFile, editFileLabel } = useFileActions();

  const removeSelectedFileParam = useCallback(() => {
    updateQueryParam({
      removeParams: [BrokerUrlParams.SELECTED_FILE],
    });
  }, [updateQueryParam]);

  const onDeleteClicked = useCallback(
    async (item: ExtendedBoxItem) => {
      await deleteFile(item);
      if (selectedFiles && setSelectedFiles) {
        setSelectedFiles(selectedFiles.filter((selectedFile) => selectedFile.id !== item.id));
      }
      removeSelectedFileParam();
      // don't await files refetch in order to proceed after delete succeeded, it redirects back to files explorer main screen and a loaderOverlay will be displayed until files are re-fetched
      afterFileOperation();
    },
    [afterFileOperation, deleteFile, removeSelectedFileParam, selectedFiles, setSelectedFiles],
  );

  const onRenameSubmit = useCallback(
    async (item: ExtendedBoxItem, newName: string) => {
      await renameFile(item, newName);
      // don't await files refetch in order to proceed after rename succeeded, it redirects back to files explorer main screen and a loaderOverlay will be displayed until files are re-fetched
      afterFileOperation();
    },
    [afterFileOperation, renameFile],
  );

  const onPreviewClicked = useCallback(
    (file: ExtendedBoxItem) => {
      hotjar.event(HotjarEvents.PreviewFile);
      updateQueryParam({
        addParams: [
          {
            queryParam: BrokerUrlParams.SELECTED_FILE,
            value: file.id,
          },
        ],
      });
    },
    [hotjar, updateQueryParam],
  );

  const fileTypeUpdateSideEffects = useCallback(
    async (file: BoxItem, selectedLabel?: string) => {
      if (!submissionId) {
        return;
      }

      const existingLabel = getFileMetadataField(file, BoxTemplateLabels.FileType);

      // edited an auto-labeled fileType - upsert prompt result
      if (getBoxClassifiedByRoleMetadata(file) === UserRole.Machine && existingLabel !== selectedLabel) {
        const data: PromptResultCreate = {
          entityId: file.id,
          input: {},
          result: existingLabel || '',
          feature: PromptResultFeature.FileClassification,
          modifiedResult: selectedLabel || '',
          submissionId,
        };
        createPromptResult.mutateAsync({ data });
      }

      if (selectedLabel === FileType.ExpiringProgram && existingLabel !== selectedLabel) {
        await extractQuoteData({
          submissionId,
          fileId: file.id,
          extractionType: ExtractionType.ExpiringPolicy,
        });
      }
    },
    [createPromptResult, extractQuoteData, submissionId],
  );

  const onLabelClicked = useCallback(
    async (file: BoxItem, labelFieldName: BoxTemplateLabels, selectedLabel?: string) => {
      await editFileLabel(file, labelFieldName, selectedLabel);

      if (submissionId && labelFieldName === BoxTemplateLabels.FileType) {
        await fileTypeUpdateSideEffects(file, selectedLabel);
      }
    },
    [fileTypeUpdateSideEffects, editFileLabel, submissionId],
  );

  const onTruncateClicked = useCallback(
    async (item: ExtendedBoxItem) => {
      if (!item) {
        return;
      }
      try {
        showProgressOverlay('', { phase: 'preparing' }, item.name);
        const result = await truncatePdf(item.id, {
          onProgress: (progress) => updateProgress(progress),
        });
        if (!result.didTruncate) {
          showToast('info', { message: result.alreadyTruncatedMessage });
          return;
        }
        showToast('success', { message: result.successMessage });
        hotjar.event(HotjarEvents.TruncateFile);
        // don't await files refetch in order to proceed after truncate succeeded, a loaderOverlay will be displayed until files are re-fetched
        afterFileOperation();
      } catch (e) {
        logger.log('error', { message: `Failed to truncate file ${item.name}`, error: e });
        showToast('error', { message: extractErrorMessage(e) });
      } finally {
        hideOverlay();
      }
    },
    [afterFileOperation, hotjar, showProgressOverlay, updateProgress, hideOverlay, showToast, truncatePdf],
  );

  return {
    onDeleteClicked,
    onRenameSubmit,
    onDownloadClicked: downloadFile,
    onPreviewClicked,
    onLabelClicked,
    onTruncateClicked,
  };
}
